import { useEffect, useState } from 'react';
import type { AdminOutletList, AdminOutletRow } from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';
import { money } from './Billing';

// Obyektlər (KT-72 hissə 3): yalnız hesablaşma və aktivlik metadatası — mal, müştəri, satış məbləği yoxdur.
// Süzgəclər URL-də saxlanır (?q=…&debt=1…), səhifədə 25 sətir. Excel ekrandakı süzgəclə eyni sətirlər (yalnız əsas admin).

const PLAN: Record<string, string> = { simple: 'Sadə', standard: 'Standart', full: 'Tam', custom: 'Özün qur' };
const STATUS: Record<string, string> = { trial: 'Sınaq', active: 'Aktiv', frozen: 'Dondurulub', overdue: 'Gecikmə', ended: 'Bitib' };
const SECTOR: Record<string, string> = { market: 'Market', supermarket: 'Supermarket', wholesale: 'Topdan', agro: 'Aqro', restaurant: 'Restoran', clothing: 'Geyim', service: 'Xidmət' };
const KEYS = ['q', 'sector', 'plan', 'status', 'debt', 'idle', 'trialEnding', 'page'] as const;
type Filters = Partial<Record<(typeof KEYS)[number], string>>;

function errText(err: unknown): string {
  return err instanceof AdminApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

function readFilters(): Filters {
  const p = new URLSearchParams(window.location.search);
  const f: Filters = {};
  for (const k of KEYS) {
    const v = p.get(k);
    if (v) f[k] = v;
  }
  return f;
}

function toQuery(f: Filters): string {
  const p = new URLSearchParams();
  for (const k of KEYS) if (f[k]) p.set(k, f[k] as string);
  return p.toString();
}

function bakuDate(iso: string | null): string {
  return iso ? new Date(Date.parse(iso) + 4 * 3600_000).toISOString().slice(0, 10) : '—';
}

export function Outlets({ isOwner, canBilling }: { isOwner: boolean; canBilling: boolean }) {
  const [f, setF] = useState<Filters>(readFilters);
  const [q, setQ] = useState(f.q ?? '');
  const [data, setData] = useState<AdminOutletList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function apply(next: Filters) {
    const clean: Filters = {};
    for (const k of KEYS) if (next[k]) clean[k] = next[k];
    setF(clean);
    const qs = toQuery(clean);
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }

  function load() {
    setError(null);
    adminApi
      .outlets(toQuery(f))
      .then(setData)
      .catch((err) => setError(errText(err)));
  }
  useEffect(load, [f]);

  async function setStatus(r: AdminOutletRow, status: 'frozen' | 'active') {
    setBusy(true);
    try {
      await adminApi.outletStatus(r.tenantId, r.outletId, status);
      load();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const page = Number(f.page ?? '1');

  return (
    <section className="card card-wide">
      <div className="toolbar">
        <h2>Obyektlər</h2>
        <span className="topbar-spacer" />
        {isOwner && (
          <a className="btn-small" href={`/api/admin/outlets.xlsx?${toQuery({ ...f, page: undefined })}`}>
            Excel
          </a>
        )}
      </div>
      <form
        className="filter-row"
        onSubmit={(e) => {
          e.preventDefault();
          apply({ ...f, q, page: undefined });
        }}
      >
        <input aria-label="Axtarış" placeholder="Ad, VÖEN, telefon" value={q} onChange={(e) => setQ(e.target.value)} />
        <select aria-label="Sahə" value={f.sector ?? ''} onChange={(e) => apply({ ...f, sector: e.target.value, page: undefined })}>
          <option value="">Bütün sahələr</option>
          {Object.entries(SECTOR).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select aria-label="Paket" value={f.plan ?? ''} onChange={(e) => apply({ ...f, plan: e.target.value, page: undefined })}>
          <option value="">Bütün paketlər</option>
          {Object.entries(PLAN).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select aria-label="Status" value={f.status ?? ''} onChange={(e) => apply({ ...f, status: e.target.value, page: undefined })}>
          <option value="">Bütün statuslar</option>
          {['trial', 'active', 'frozen'].map((k) => (
            <option key={k} value={k}>
              {STATUS[k]}
            </option>
          ))}
        </select>
        <select aria-label="Aktivlik" value={f.idle ?? ''} onChange={(e) => apply({ ...f, idle: e.target.value, page: undefined })}>
          <option value="">Aktivlik: hamısı</option>
          <option value="7">7+ gün satışsız</option>
          <option value="30">30+ gün satışsız</option>
        </select>
        {f.trialEnding && (
          <button type="button" className="btn-small" onClick={() => apply({ ...f, trialEnding: undefined, page: undefined })}>
            Sınağı {f.trialEnding} gündə bitən ✕
          </button>
        )}
        <label className="check">
          <input type="checkbox" checked={f.debt === '1'} onChange={(e) => apply({ ...f, debt: e.target.checked ? '1' : undefined, page: undefined })} /> Gecikən borc
        </label>
        <button type="submit" className="btn-small">
          Axtar
        </button>
      </form>
      {error && <p className="bad" role="alert">{error}</p>}
      {!data && !error && <p className="muted">Yüklənir…</p>}
      {data && data.items.length === 0 && <p className="muted">Uyğun obyekt yoxdur.</p>}
      {data && data.items.length > 0 && (
        <>
          <div className="table-scroll">
            <table className="plain">
              <thead>
                <tr>
                  <th>Şirkət · obyekt</th>
                  <th className="narrow-hide">Sahə</th>
                  <th>Paket · status</th>
                  <th>Növbəti ödəniş</th>
                  <th className="num">Gecikən borc</th>
                  <th className="narrow-hide">Son aktivlik</th>
                  <th className="narrow-hide">Sahib</th>
                  {canBilling && <th />}
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.outletId}>
                    <td>
                      <strong>{r.tenantName}</strong>
                      {r.voen && <span className="muted mono small"> · {r.voen}</span>}
                      <div className="muted small">{r.outletName}</div>
                    </td>
                    <td className="narrow-hide">{SECTOR[r.sector] ?? r.sector}</td>
                    <td>
                      {r.planCode ? (PLAN[r.planCode] ?? r.planCode) : '—'}
                      <div className="muted small">
                        {r.subStatus ? (STATUS[r.subStatus] ?? r.subStatus) : '—'}
                        {r.subStatus === 'trial' && r.trialUntil && ` · ${r.trialUntil}-dək`}
                      </div>
                    </td>
                    <td className="nowrap">
                      {r.nextDue ?? '—'}
                      {r.nextAmount && <div className="muted small">{money(r.nextAmount)}</div>}
                    </td>
                    <td className={`num nowrap ${r.debt !== '0.00' ? 'bad' : ''}`}>{r.debt !== '0.00' ? money(r.debt) : '—'}</td>
                    <td className="narrow-hide small nowrap">
                      Satış: {bakuDate(r.lastSale)}
                      <div className="muted">Giriş: {bakuDate(r.lastLogin)}</div>
                      {r.idleDays >= 7 && <div className="muted">{r.idleDays} gün satışsız</div>}
                    </td>
                    <td className="narrow-hide small">
                      {r.ownerName ?? '—'}
                      <div className="muted">{r.ownerPhone}</div>
                    </td>
                    {canBilling && (
                      <td>
                        {r.subStatus === 'frozen' ? (
                          <button type="button" className="btn-small" disabled={busy} onClick={() => setStatus(r, 'active')}>
                            Aktivləşdir
                          </button>
                        ) : r.subStatus ? (
                          <button type="button" className="btn-small" disabled={busy} onClick={() => setStatus(r, 'frozen')}>
                            Dondur
                          </button>
                        ) : null}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="toolbar">
            <span className="muted small">
              {data.total} obyekt · səhifə {page} / {pages}
            </span>
            <span className="topbar-spacer" />
            <button type="button" className="btn-small" disabled={page <= 1} onClick={() => apply({ ...f, page: String(page - 1) })}>
              ← Əvvəlki
            </button>
            <button type="button" className="btn-small" disabled={page >= pages} onClick={() => apply({ ...f, page: String(page + 1) })}>
              Növbəti →
            </button>
          </div>
          <p className="muted small">Dondurmada kassada satış dayanmır — yalnız idarə funksiyaları məhdudlaşır.</p>
        </>
      )}
    </section>
  );
}
